import type { GameState, MapDefinition, TileData, TilePosition } from '../../model/gamestate/GameState';
import { PortalTransitionAnimator } from '../skillVisualEffects/PortalTransitionAnimator';
import { getGrid } from '../../grid';
import { ExplosionEffect } from '../skillVisualEffects/ExplosionEffect';
import { SmiteEffect } from '../skillVisualEffects/SmiteEffect';
import { CastTargetEffect } from '../skillVisualEffects/CastTargetEffect';
import type { ActiveZoneData } from '../../spacetime/modules/SkillModule';
import { playSharedMapAnimation } from '../mapEvents/effectPlayback';

import { COLORS, DEFAULT_FRAME_DELTA_MS, MAX_FRAME_DELTA_MS } from './constants';
import { Viewport } from './viewport';
import { MapLayerRenderer } from './MapLayerRenderer';
import { StaticObjectLayerRenderer } from './StaticObjectLayerRenderer';
import { resolveTileDrawMetrics } from './board';
import { EntityManager } from './EntityManager';
import { InteractionManager } from './InteractionManager';
import { UIRenderer } from './UIRenderer';
import type { TileMapRendererOptions, VisualEffect, TileSelectedDetail, EntityProfileDetail, EntityContextActionDetail, ActorTargetDetail, HoveredActorInfo, PixelPosition } from './types';
import { subscribeToSpeechBubbleUpdates } from '../../chat/speechBubbles';
import { resolveRemainingTicks, withParsedPayload } from '../mapEvents/animationHandlers';

export class TileMapRenderer extends EventTarget {
    public static async create(options: TileMapRendererOptions): Promise<TileMapRenderer> {
        const renderer = new TileMapRenderer(options);
        await renderer.initialize();
        return renderer;
    }

    private readonly container: HTMLElement;
    private readonly tileSize: number;
    private gameState: GameState;
    private currentMapId: string;
    private destroyed = false;

    private readonly backgroundCanvas: HTMLCanvasElement;
    private readonly lowerOverlayCanvas: HTMLCanvasElement;
    private readonly staticObjectCanvas: HTMLCanvasElement;
    private readonly animatedObjectOverlay: HTMLDivElement;
    private readonly canvas: HTMLCanvasElement;
    private lowerOverlayCtx: CanvasRenderingContext2D | null = null;
    private ctx: CanvasRenderingContext2D | null = null;

    public viewport: Viewport;
    public mapLayer: MapLayerRenderer;
    private staticObjectLayer: StaticObjectLayerRenderer;
    private entityManager: EntityManager;
    private interactionManager: InteractionManager;
    private uiRenderer: UIRenderer;

    private portalTransition: PortalTransitionAnimator | null = null;
    private resizeObserver: ResizeObserver | null = null;

    private cameraFocus: TilePosition | null = null;
    private cameraFocusWorldCenter: PixelPosition | null = null;
    private lastCharacterPosition: TilePosition | null = null;
    private selfSnapTarget: TilePosition | null = null;
    private localCharacterRotation: number = 180;
    private playerDisplayName: string = '';

    private animationFrameId: number | null = null;
    private lastFrameTimestamp: number | null = null;
    private pendingRender = false;
    private isRenderingFrame = false;
    private activeZones: ActiveZoneData[] = [];
    private lastPoisonFogLogTick = -1;
    private canvasLogicalWidth = 0;
    private canvasLogicalHeight = 0;
    private canvasDevicePixelRatio = 1;
    private readonly unsubscribeSpeechBubbleUpdates: () => void;
    private ghostTileSelectionEnabled = false;

    private constructor(options: TileMapRendererOptions) {
        super();
        this.container = options.container;
        this.tileSize = options.tileSize ?? 48;
        this.gameState = options.gameState;
        this.currentMapId = options.gameState.getCurrentMapId();

        this.backgroundCanvas = document.createElement('canvas');
        this.backgroundCanvas.style.position = 'absolute';
        this.backgroundCanvas.style.top = '0';
        this.backgroundCanvas.style.left = '0';
        this.backgroundCanvas.style.pointerEvents = 'none';

        this.lowerOverlayCanvas = document.createElement('canvas');
        this.lowerOverlayCanvas.style.position = 'absolute';
        this.lowerOverlayCanvas.style.top = '0';
        this.lowerOverlayCanvas.style.left = '0';
        this.lowerOverlayCanvas.style.pointerEvents = 'none';

        this.staticObjectCanvas = document.createElement('canvas');
        this.staticObjectCanvas.style.position = 'absolute';
        this.staticObjectCanvas.style.top = '0';
        this.staticObjectCanvas.style.left = '0';
        this.staticObjectCanvas.style.pointerEvents = 'none';

        this.animatedObjectOverlay = document.createElement('div');
        this.animatedObjectOverlay.style.position = 'absolute';
        this.animatedObjectOverlay.style.top = '0';
        this.animatedObjectOverlay.style.left = '0';
        this.animatedObjectOverlay.style.pointerEvents = 'none';
        this.animatedObjectOverlay.style.overflow = 'hidden';

        this.canvas = document.createElement('canvas');
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.touchAction = 'none';
        this.canvas.style.userSelect = 'none';
        this.canvas.style.cursor = '';

        if (getComputedStyle(this.container).position === 'static') {
            this.container.style.position = 'relative';
        }
        this.container.style.overflow = 'hidden';
        this.container.style.backgroundColor = COLORS.background;

        this.viewport = new Viewport(this.tileSize);
        this.mapLayer = new MapLayerRenderer(
            this.tileSize,
            options.map,
            this.backgroundCanvas,
            () => this.scheduleRender()
        );
        this.staticObjectLayer = new StaticObjectLayerRenderer(
            this.tileSize,
            options.map,
            this.staticObjectCanvas,
            this.animatedObjectOverlay,
            () => this.scheduleRender()
        );
        this.entityManager = new EntityManager();
        this.uiRenderer = new UIRenderer(
            this.container,
            this.canvas,
            this.tileSize,
            this,
            () => this.activeRadius,
            () => this.scheduleRender(),
        );

        this.interactionManager = new InteractionManager(
            this.canvas,
            this.viewport,
            this.mapLayer,
            this.entityManager,
            this,
            this.tileSize,
            {
                isPortalTransitioning: () => this.isPortalTransitioning(),
                getLastCharacterPosition: () => this.lastCharacterPosition,
                showEntityContextMenu: (pointer, actor) => this.uiRenderer.showEntityContextMenu(pointer, actor),
                hideEntityContextMenu: () => this.uiRenderer.hideEntityContextMenu(),
                requestRender: () => this.scheduleRender(),
                getActiveRadius: () => this.activeRadius,
                allowGhostTileSelection: () => this.ghostTileSelectionEnabled,
            }
        );
        this.unsubscribeSpeechBubbleUpdates = subscribeToSpeechBubbleUpdates(() => this.scheduleRender());
    }

    public get view(): HTMLElement | null {
        return this.destroyed ? null : this.canvas;
    }

    public get activeRadius(): number {
        return this.gameState.getViewRadius();
    }

    public getCameraFocus(): TilePosition | null {
        return this.cameraFocus;
    }

    public getCurrentMap(): MapDefinition {
        return this.mapLayer.getMap();
    }

    public getCurrentGrid() {
        return this.mapLayer.getGrid();
    }

    public getViewSize(): { width: number; height: number } {
        return {
            width: this.canvasLogicalWidth,
            height: this.canvasLogicalHeight,
        };
    }

    public worldToScreenCenter(x: number, y: number): PixelPosition {
        return this.viewport.worldToScreenCenter(x, y, this.mapLayer.getGrid());
    }

    public worldCenterToScreen(center: PixelPosition): PixelPosition {
        return this.viewport.worldCenterToScreen(center);
    }

    public mapPositionToScreen(mapX: number, mapY: number): PixelPosition {
        return this.viewport.worldCenterToScreen({
            x: mapX * this.tileSize,
            y: mapY * this.tileSize,
        });
    }

    public screenToTile(screenX: number, screenY: number): TilePosition | null {
        const map = this.mapLayer.getMap();
        const grid = this.mapLayer.getGrid();
        return this.viewport.screenToTile(screenX, screenY, grid, map.width, map.height);
    }

    public screenToMapPosition(event: MouseEvent | DragEvent): PixelPosition | null {
        const canvas = this.view;
        if (!(canvas instanceof HTMLCanvasElement)) {
            return null;
        }
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) {
            return null;
        }
        const screenX = event.clientX - rect.left;
        const screenY = event.clientY - rect.top;
        if (!this.screenToTile(screenX, screenY)) {
            return null;
        }
        const world = this.viewport.screenToWorld(screenX, screenY);
        return {
            x: world.x / this.tileSize,
            y: world.y / this.tileSize,
        };
    }

    public setCameraFocus(position: TilePosition): void {
        this.cameraFocus = { ...position };
        this.cameraFocusWorldCenter = this.resolveWorldCenter(position);
        this.scheduleRender();
    }

    public panCameraByScreen(deltaX: number, deltaY: number): void {
        const baseCenter = this.cameraFocusWorldCenter
            ?? (this.cameraFocus ? this.resolveWorldCenter(this.cameraFocus) : null)
            ?? (this.lastCharacterPosition ? this.resolveWorldCenter(this.lastCharacterPosition) : null);
        if (!baseCenter) {
            return;
        }
        this.cameraFocusWorldCenter = {
            x: baseCenter.x + deltaX,
            y: baseCenter.y + deltaY,
        };
        this.scheduleRender();
    }

    public rotateCameraOrbit(_direction: 'left' | 'right'): void {
        // 2D view has no perspective orbit; keep Q/E harmless when 2D is selected.
    }

    public getVisualCharacterPosition(): TilePosition | null {
        const selfState = this.entityManager.getSelfActorState(this.gameState);
        return selfState?.current ?? this.lastCharacterPosition;
    }

    public markActorSnap(entityId: string, target: TilePosition): void {
        this.entityManager.markActorSnap(entityId, target);
    }

    public setCharacterPosition(position: TilePosition, options?: { immediate?: boolean; rotation?: number }): void {
        const previousTarget = this.lastCharacterPosition;
        this.lastCharacterPosition = { ...position };
        const authoritativeRotation =
            options?.rotation !== undefined
                ? options.rotation
                : this.gameState.getCharacterRotation();
        this.localCharacterRotation = authoritativeRotation;

        const shouldSnap = options?.immediate || !previousTarget;
        if (shouldSnap) {
            this.selfSnapTarget = { ...position };
            this.uiRenderer.setActiveRangeCenter({ ...position });
            if (!this.cameraFocus) {
                this.cameraFocus = { ...position };
            }
        } else {
            const activeCenter = this.uiRenderer.getActiveRangeCenter();
            if (!activeCenter || activeCenter.x !== position.x || activeCenter.y !== position.y) {
                this.uiRenderer.setActiveRangeCenter({ ...position });
            }
        }

        this.scheduleRender();
    }

    public setQueuePreview(queue: (TilePosition | null)[]): void {
        this.uiRenderer.setQueuePreview(queue);
        this.scheduleRender();
    }

    public setActiveZones(zones: ActiveZoneData[]): void {
        this.activeZones = zones;
        this.scheduleRender();
    }

    public requestRender(): void {
        const currentMap = this.gameState.getMap();
        if (currentMap !== this.mapLayer.getMap()) {
            const nextMapId = this.gameState.getCurrentMapId();
            if (nextMapId !== this.currentMapId) {
                this.swapToNewMap(currentMap, nextMapId);
                return;
            }
            this.mapLayer.setMap(currentMap);
            this.staticObjectLayer.setMap(currentMap);
            this.syncCanvasDimensions();
        }
        this.scheduleRender();
    }

    public setPlayerDisplayName(displayName: string): void {
        this.playerDisplayName = displayName;
        const selfState = this.entityManager.getSelfActorState(this.gameState);
        if (selfState) {
            selfState.data = { ...selfState.data, displayName: displayName || undefined };
        }
        this.scheduleRender();
    }

    public setSkillTargetingActive(active: boolean): void {
        this.interactionManager.setSkillTargetingActive(active);
    }

    public addEffect(effect: VisualEffect): void {
        if (this.isPortalTransitioning()) {
            effect.disposeScene?.();
            return;
        }
        this.entityManager.addEffect(effect);
        this.scheduleRender();
    }

    public isPortalTransitioning(): boolean {
        return !!this.portalTransition?.isActive();
    }

    public startPortalTransition(onFadeOutComplete: () => void | Promise<void>, onComplete: () => void): void {
        if (this.portalTransition?.isActive()) {
            console.warn('[Renderer] Portal transition already in progress');
            return;
        }

        this.entityManager.clearEffects();
        this.portalTransition = new PortalTransitionAnimator();
        this.portalTransition.startTransition({
            onFadeOutComplete,
            onComplete: () => {
                this.portalTransition = null;
                onComplete();
            }
        });
        this.scheduleRender();
    }

    public cancelPortalTransition(): void {
        if (!this.portalTransition) {
            return;
        }
        this.portalTransition.cancelAnimation();
        this.portalTransition = null;
        this.scheduleRender();
    }

    public playAnimation(animationName: string, payloadJson: string): void {
        const grid = this.mapLayer.getGrid();
        if (playSharedMapAnimation({
            animationName,
            payloadJson,
            tileSize: this.tileSize,
            tileToScreenCenter: (x, y) => this.viewport.worldToScreenCenter(x, y, grid),
            resolveEntityPosition: (entityId) => this.resolveRenderableEntityPosition(entityId),
            addEffect: (effect) => this.addEffect(effect),
        })) {
            return;
        }

        if (animationName === 'skill_cast_start') {
            withParsedPayload<{ skillId?: string; targetX?: number; targetY?: number; resolveTick?: number }>('skill_cast_start', payloadJson, (payload) => {
                const { skillId, targetX, targetY, resolveTick } = payload;
                if (typeof targetX !== 'number' || typeof targetY !== 'number') {
                    return;
                }
                const isMeteor = skillId === 'summon_meteor';
                this.addEffect(new CastTargetEffect({
                    x: targetX,
                    y: targetY,
                    radiusTiles: isMeteor ? 2 : 0.75,
                    durationSeconds: resolveRemainingTicks(this.gameState.getTick(), { skillId, targetX, targetY, resolveTick }),
                    tileToScreenCenter: (tx: number, ty: number) => this.viewport.worldToScreenCenter(tx, ty, grid),
                    tileSize: this.tileSize,
                    strokeColor: isMeteor ? 'rgba(255, 145, 77, 0.95)' : 'rgba(250, 204, 21, 0.9)',
                    fillColor: isMeteor ? 'rgba(153, 27, 27, 0.55)' : 'rgba(180, 83, 9, 0.3)',
                }));
            });
        } else if (animationName === 'summon_meteor') {
            try {
                const payload = JSON.parse(payloadJson);
                const { x, y, radius } = payload;
                if (typeof x === 'number' && typeof y === 'number') {
                    const safeRadius = typeof radius === 'number' && Number.isFinite(radius) && radius >= 0 ? radius : 2;
                    this.addEffect(new SmiteEffect({
                        target: { x, y },
                        tileToScreenCenter: (tx: number, ty: number) => this.viewport.worldToScreenCenter(tx, ty, grid),
                        tileSize: this.tileSize,
                    }));
                    this.addEffect(new ExplosionEffect({
                        x,
                        y,
                        radius: safeRadius,
                        tileToScreenCenter: (tx: number, ty: number) => this.viewport.worldToScreenCenter(tx, ty, grid),
                        tileSize: this.tileSize,
                    }));
                }
            } catch (e) {
                console.error('Failed to parse summon_meteor payload', e);
            }
        }
    }

    private resolveRenderableEntityPosition(entityId: string): { x: number; y: number } | null {
        const visuals = this.entityManager.getActorVisuals();
        const entityNode = visuals.get(entityId)
            || (entityId === this.gameState.getSelfEntityId()
                ? this.entityManager.getSelfActorState(this.gameState)
                : null);
        return entityNode ? { x: entityNode.current.x, y: entityNode.current.y } : null;
    }

    public startPortalFadeIn(): void {
        if (!this.portalTransition) {
            console.warn('[Renderer] No portal transition to fade in from');
            return;
        }
        this.portalTransition.startFadeIn();
        this.scheduleRender();
    }

    public swapToNewMap(map: MapDefinition, mapId: string): void {
        if (map.width <= 0 || map.height <= 0) {
            console.error('[RENDERER] Invalid map dimensions caught in swapToNewMap', map);
        }

        this.currentMapId = mapId;
        this.mapLayer.setMap(map);
        this.staticObjectLayer.setMap(map);
        this.entityManager.clear();
        this.activeZones = [];
        this.lastPoisonFogLogTick = -1;

        this.syncCanvasDimensions();

        this.scheduleRender();
    }

    public refreshCurrentMap(map: MapDefinition): void {
        if (map.width <= 0 || map.height <= 0) {
            console.error('[RENDERER] Invalid map dimensions caught in refreshCurrentMap', map);
        }
        this.currentMapId = this.gameState.getCurrentMapId();
        this.mapLayer.setMap(map);
        this.staticObjectLayer.setMap(map);
        this.syncCanvasDimensions();
        this.scheduleRender();
    }

    public destroy(): void {
        this.destroyed = true;
        this.unsubscribeSpeechBubbleUpdates();
        this.activeZones = [];
        this.lastPoisonFogLogTick = -1;
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.mapLayer.destroy();
        this.staticObjectLayer.destroy();
        this.uiRenderer.destroy();
        this.interactionManager.detachPointerEvents();
        this.stopRenderLoop();
        this.lowerOverlayCtx = null;
        this.ctx = null;
        this.container.innerHTML = '';
    }

    // Initialization

    private async initialize(): Promise<void> {
        this.container.innerHTML = '';
        this.container.appendChild(this.backgroundCanvas);
        this.container.appendChild(this.lowerOverlayCanvas);
        this.container.appendChild(this.staticObjectCanvas);
        this.container.appendChild(this.animatedObjectOverlay);
        this.container.appendChild(this.canvas);
        this.syncCanvasDimensions();
        this.setupResizeObserver();

        this.lowerOverlayCtx = this.lowerOverlayCanvas.getContext('2d');
        if (!this.lowerOverlayCtx) {
            console.error('[TileMapRenderer2D] Failed to initialize lower overlay context');
            return;
        }
        this.ctx = this.canvas.getContext('2d');
        if (!this.ctx) {
            console.error('[TileMapRenderer2D] Failed to initialize rendering context');
            return;
        }
        this.syncCanvasDimensions();
        this.lowerOverlayCtx.imageSmoothingEnabled = true;
        this.ctx.imageSmoothingEnabled = true;

        this.interactionManager.attachPointerEvents();
        this.addEventListener('pathTilePlacementPreviewChanged', this.handlePathTilePlacementPreviewChanged as EventListener);
        this.scheduleRender();
    }

    private readonly handlePathTilePlacementPreviewChanged = (event: Event): void => {
        const detail = (event as CustomEvent<{ active?: boolean; autotile?: boolean }>).detail;
        this.ghostTileSelectionEnabled = detail?.active === true && detail?.autotile === true;
    };

    private setupResizeObserver(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(() => {
            this.syncCanvasDimensions();
            this.scheduleRender();
        });
        this.resizeObserver.observe(this.container);
    }

    private syncCanvasDimensions(): void {
        const containerRect = this.container.getBoundingClientRect();
        this.viewport.setContainerRect(containerRect);

        // Background map layer knows its own pixel sizes perfectly from MapDef
        this.mapLayer.syncCanvasDimensions(containerRect.width, containerRect.height);
        this.staticObjectLayer.syncCanvasDimensions(containerRect.width, containerRect.height);

        const viewportWidth = Math.max(1, Math.round(containerRect.width));
        const viewportHeight = Math.max(1, Math.round(containerRect.height));
        const devicePixelRatio = window.devicePixelRatio || 1;
        const backingWidth = Math.max(1, Math.round(viewportWidth * devicePixelRatio));
        const backingHeight = Math.max(1, Math.round(viewportHeight * devicePixelRatio));

        this.canvasLogicalWidth = viewportWidth;
        this.canvasLogicalHeight = viewportHeight;
        this.canvasDevicePixelRatio = devicePixelRatio;

        this.lowerOverlayCanvas.style.width = `${viewportWidth}px`;
        this.lowerOverlayCanvas.style.height = `${viewportHeight}px`;
        this.lowerOverlayCanvas.width = backingWidth;
        this.lowerOverlayCanvas.height = backingHeight;
        this.canvas.style.width = `${viewportWidth}px`;
        this.canvas.style.height = `${viewportHeight}px`;
        this.canvas.width = backingWidth;
        this.canvas.height = backingHeight;

        if (this.lowerOverlayCtx) {
            this.lowerOverlayCtx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
            this.lowerOverlayCtx.imageSmoothingEnabled = true;
        }
        if (this.ctx) {
            this.ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
            this.ctx.imageSmoothingEnabled = true;
        }
    }

    private stopRenderLoop(): void {
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        this.pendingRender = false;
    }

    private scheduleRender(): void {
        if (this.destroyed) return;
        this.pendingRender = true;
        if (this.isRenderingFrame) return;
        if (this.animationFrameId === null) {
            this.animationFrameId = requestAnimationFrame(this.handleAnimationFrame);
        }
    }

    private readonly handleAnimationFrame = (time: number): void => {
        this.animationFrameId = null;
        this.pendingRender = false;
        this.isRenderingFrame = true;
        const shouldContinue = this.renderScene(time);
        this.isRenderingFrame = false;
        if (shouldContinue || this.pendingRender) {
            this.animationFrameId = requestAnimationFrame(this.handleAnimationFrame);
        } else {
            this.lastFrameTimestamp = null;
        }
    };

    private updateCamera(position: TilePosition): void {
        const worldCenter = this.cameraFocusWorldCenter ?? this.resolveWorldCenter(position);
        this.viewport.updateCameraFromWorldCenter(worldCenter, this.container);
    }

    private resolveWorldCenter(position: TilePosition): PixelPosition {
        const grid = this.mapLayer.getGrid();
        const tileMetrics = resolveTileDrawMetrics(grid, this.tileSize, position.x, position.y);
        const centroid = this.viewport.getInterpolatedTileCentroid(position.x, position.y, grid);
        return {
            x: tileMetrics.originX + centroid.x,
            y: tileMetrics.originY + centroid.y,
        };
    }

    private renderScene(timestamp?: number): boolean {
        if (!this.ctx || !this.lowerOverlayCtx) return false;
        const ctx = this.ctx;
        const lowerOverlayCtx = this.lowerOverlayCtx;

        const deltaMsRaw = timestamp !== undefined && this.lastFrameTimestamp !== null
            ? timestamp - this.lastFrameTimestamp
            : DEFAULT_FRAME_DELTA_MS;
        const deltaMs = Math.min(Math.max(deltaMsRaw, 0), MAX_FRAME_DELTA_MS);
        const deltaSeconds = deltaMs / 1000;
        this.lastFrameTimestamp = timestamp ?? this.lastFrameTimestamp ?? performance.now();

        const map = this.mapLayer.getMap();

        if (!map || map.width <= 0 || map.height <= 0) {
            if (!this.destroyed) console.warn('[RENDERER] renderScene called with invalid map', map);
            return false;
        }

        let isAnimatingActors = false;
        const { hasActiveAnimation, clearedSnapTarget } = this.entityManager.updateActorVisuals(
            deltaSeconds,
            this.gameState,
            this.lastCharacterPosition,
            this.selfSnapTarget,
            this.localCharacterRotation,
            this.playerDisplayName,
            (position) => this.resolveWorldCenter(position)
        );
        isAnimatingActors = hasActiveAnimation;
        if (clearedSnapTarget) this.selfSnapTarget = null;
        this.interactionManager.checkHoveredActorExists();

        const selfState = this.entityManager.getSelfActorState(this.gameState);

        // Camera positioning
        if (this.cameraFocus) {
            this.updateCamera(this.cameraFocus);
        } else if (selfState) {
            this.viewport.updateCameraFromWorldCenter(
                selfState.currentCenter,
                this.container,
            );
        } else {
            if (this.lastCharacterPosition) {
                this.updateCamera(this.lastCharacterPosition);
            }
        }

        // Drawing sequence

        // 1. Draw static background
        this.mapLayer.drawBackground(this.viewport);

        // 2. Clear lower overlay
        lowerOverlayCtx.setTransform(1, 0, 0, 1, 0, 0);
        lowerOverlayCtx.clearRect(0, 0, this.lowerOverlayCanvas.width, this.lowerOverlayCanvas.height);
        lowerOverlayCtx.setTransform(
            this.canvasDevicePixelRatio,
            0,
            0,
            this.canvasDevicePixelRatio,
            0,
            0,
        );

        // 3. Draw under-object overlays
        this.uiRenderer.drawActiveTiles(lowerOverlayCtx, this.mapLayer, this.viewport);
        this.uiRenderer.drawHoverHighlight(lowerOverlayCtx, this.interactionManager.getHoveredTile(), this.mapLayer, this.viewport);
        this.uiRenderer.drawPathTilePlacementPreview(lowerOverlayCtx, this.interactionManager.getHoveredTile(), this.viewport, this.mapLayer);
        this.uiRenderer.drawBiomeRegionPlacementPreview(lowerOverlayCtx, this.viewport, this.mapLayer);
        this.uiRenderer.drawMapObjectPlacementPreview(lowerOverlayCtx, this.interactionManager.getHoveredTile(), this.viewport, this.mapLayer);
        this.uiRenderer.drawPathLines(lowerOverlayCtx, selfState, this.lastCharacterPosition, this.viewport, this.mapLayer);
        this.uiRenderer.drawQueueMarkers(lowerOverlayCtx, selfState, this.lastCharacterPosition, this.viewport, this.mapLayer);

        // 4. Draw static world objects above low overlays
        this.staticObjectLayer.draw(this.viewport);

        // 5. Clear upper dynamic foreground
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        ctx.setTransform(
            this.canvasDevicePixelRatio,
            0,
            0,
            this.canvasDevicePixelRatio,
            0,
            0,
        );

        // 6. Draw runtime world objects
        this.uiRenderer.drawNpcs(ctx, this.gameState.getNpcs(), this.viewport, this.mapLayer);
        this.uiRenderer.drawLootChests(ctx, this.gameState.getNpcRuntime().filter(n => n.npcType === 'chest'), this.viewport, this.mapLayer);

        // 7. Draw dynamic entities
        const hasSpeechBubbles = this.uiRenderer.drawActors(ctx, this.entityManager, this.lastCharacterPosition, this.viewport, this.mapLayer);

        // 8. Portal overlay
        const hasPortalTransition = Boolean(this.portalTransition?.isActive());
        if (hasPortalTransition && this.portalTransition) {
            this.portalTransition.drawOverlay(ctx, this.canvasLogicalWidth, this.canvasLogicalHeight);
        }

        // 9. Draw active zone overlays (poison fog rendered from active_zones table data)
        this.drawActiveZones(ctx);

        // 10. Process visual effects
        const effectsActive = this.entityManager.updateEffects(deltaSeconds);
        this.entityManager.renderEffectsOverlay(ctx);

        // 11. Dim out-of-range world content exactly once across all stacked world layers.
        this.applyWorldFogOverlay(ctx);

        // 12. Draw screen-space overlays
        this.uiRenderer.drawHoveredActorTooltip(ctx, this.interactionManager);

        return isAnimatingActors
            || hasPortalTransition
            || effectsActive
            || hasSpeechBubbles
            || this.staticObjectLayer.hasAnimatedObjects();
    }

    private drawActiveZones(ctx: CanvasRenderingContext2D): void {
        if (this.activeZones.length === 0) return;
        const tick = this.gameState.getTick();
        const grid = this.mapLayer.getGrid();
        let renderedPoisonFog = 0;

        for (const zone of this.activeZones) {
            if (zone.zoneType !== 'poison_fog' && zone.zoneType !== 'poison_cloud') continue;
            const center = this.viewport.worldToScreenCenter(zone.centerX, zone.centerY, grid);
            const drawRadius = (zone.radius + 0.75) * this.tileSize;
            const gradient = ctx.createRadialGradient(
                center.x, center.y, drawRadius * 0.25, center.x, center.y, drawRadius
            );
            if (zone.zoneType === 'poison_cloud') {
                gradient.addColorStop(0, 'rgba(160, 215, 90, 0.23)');
                gradient.addColorStop(0.55, 'rgba(130, 185, 70, 0.16)');
                gradient.addColorStop(1, 'rgba(90, 145, 45, 0)');
            } else {
                gradient.addColorStop(0, 'rgba(120, 200, 70, 0.20)');
                gradient.addColorStop(0.55, 'rgba(90, 170, 55, 0.14)');
                gradient.addColorStop(1, 'rgba(70, 130, 40, 0)');
            }
            ctx.save();
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(center.x, center.y, drawRadius, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
            renderedPoisonFog += 1;
        }

        if (renderedPoisonFog > 0 && tick !== this.lastPoisonFogLogTick) {
            this.lastPoisonFogLogTick = tick;
            console.log(`[ZoneRender] Rendering poison fog at tick ${tick} (zones=${renderedPoisonFog})`);
        }
    }

    private applyWorldFogOverlay(ctx: CanvasRenderingContext2D): void {
        const center = this.gameState.getCharacterPosition() ?? this.lastCharacterPosition;
        const map = this.mapLayer.getMap();
        if (!center || map.visibilityMode === 'full_map') {
            return;
        }

        const grid = this.mapLayer.getGrid();
        const radius = this.activeRadius;
        ctx.save();
        ctx.globalAlpha = 0.16;
        ctx.fillStyle = COLORS.tileWall;

        for (let y = 0; y < map.height; y += 1) {
            for (let x = 0; x < map.width; x += 1) {
                if (grid.distance(center.x, center.y, x, y) <= radius) {
                    continue;
                }
                const screen = this.viewport.worldToScreen(x, y, grid);
                const cx = screen.x + this.tileSize / 2;
                const cy = screen.y + this.tileSize / 2;
                grid.fillTile(ctx, cx, cy, this.tileSize * 0.5, x, y);
            }
        }

        ctx.restore();
    }
}

export * from './types';
export * from './constants';
